import { AdvancedFilters } from 'quotient-ng-core';
import { SortDescriptor } from '@progress/kendo-data-query';
import { GridPagination } from './event.interface';
import {
    ActivationType,
    CategoryOverrideReason,
    CPAPicklistMap,
    OfferStageName,
    PlacementType,
    ProductOfferType
} from './cpa-picklist.models';

export interface Offer {
    id: number;
    reference_id: string;
    code: string; // offer CID
    name: string;
    schedule_ref?: string;
    targeting_type: TargetingType;
    activation_type: ActivationType;
    placement_type: PlacementType;
    pods: { pod_ref: string }[];
    throttling_ref: string[];
    offer_score?: number;
    partner_offer_code: string;
    gtin_adjustments?: GtinAdjustment[];
    is_publishing_overridden: boolean;
    publishing_type: string;
    redemption_location_type: string;
    is_redemption_location_eligible: boolean;
    smsrebate_keyword?: string;
    smsrebate_welcome_message?: string;
    static_smsrebate_welcome_message?: string;
    smsrebate_shortcode?: string;
    smsrebate_agegate_required?: boolean;
    smsrebate_agegate_image?: { $ref?: string; encoded_bytes?: string };
    smsrebate_agegate_disclaimer?: string;
    riq_offer_id: string;
    linked_program_offer_codes: string[];
    partner_offer_type?: PartnerOfferType;
    clearing_ref?: string;
    barcodes?: Barcode[];
    digital_static_bricks_URL?: string;
    digital_brick_dynamic_url_count?: number;
    process_stage: ProcessStage;
    bricks_offer?: boolean;
    otp_required?: boolean;
    retailer_banners: RetailerBanner[];
    brand: string;
    product_offer_type: ProductOfferType;
    inlane_offer?: boolean;
    pod_type?: string;
}

export interface Promotion {
    id: number;
    name: string;
    type: PromotionAccountType;
    code: string;
    account: PromotionAccount;
    schedules: Schedule[];
    offers: Offer[];
    pods: Pod[];
    promotion_images?: PromotionImage[];
    insertion_order: {
        insertion_order_number: string;
        account: Account;
    };
    throttling: Throttling[];
    partner_promotion_source: string;
    savings?: Savings;
    tracking_property: PromotionTrackingProperty;
    barcodes: Barcode[];
    status: PromotionStatus;
    program_trigger?: ProgramTrigger;
    program_properties: ProgramProperties;
    categories: Category[];
    overriden_reason: CPAPicklistMap<string>;
    is_auto_generated: boolean;
    partner_category: PartnerCategory;
    is_shopper_exclusion_enabled?: boolean;
    is_promised_program?: boolean;
    is_multitrip_program?: boolean;
    is_failsafe_program?: boolean;
    clearings?: Clearing[];
    start_date?: string;
    end_date?: string;
    summary?: Summary[];
    image_url?: string;
}
export interface Account {
    name: string;
    type: string;
}

export interface TemplateType {
    print_template_types: TemplateTypeName[];
}

export interface TemplateTypeName {
    print_template_type_name: string;
}

export interface Summary {
    id?: number;
    savings: string;
}

export interface Category {
    id: number;
    is_auto_generated: boolean;
    overridden_reason?: CategoryOverrideReason;
}

export interface CategoriesUpdatePayload extends Categories {
    promotionId: number;
    offerId: number;
    partner_category?: PartnerCategory;
}

export interface PromotionPackshotPayload {
    promotionId: number;
    promotion_images: PromotionImages;
    packshot_images: PackshotImage[];
    podID: number;
    offerId?: number;
    readonly promotion?: Promotion;
}

export interface Categories extends Pick<Promotion, 'categories'> {}

export interface CategoriesPayload extends Categories {
    promotionId: number;
    offerId: number;
}

export interface PartnerCategory {
    level_1: string;
    level_2: string;
    level_3: string;
    level_4: string;
    level_5: string;
}

export interface ProgramTrigger {
    tender_types: string[];
}

export interface ProgramProperties {
    failsafe_activation_limit: number;
}

export enum PromotionStatus {
    ACTION_REQUIRED = 'ActionRequired',
    IN_PROGRESS = 'InProgress'
}

export class Barcode {
    barcode?: number;
    type?: string;
    savings_value?: {
        savings_type;
    };
}

export interface Savings {
    product_restrictions: ProductRestriction[];
    inlane_attribute?: InlaneSettings;
    basket_level_discount: boolean;
    purchase_conditions: {
        purchase_qty: string;
        product_restrictions_ref: string[];
        qty_unit_of_measure?: string;
    }[];
    rewards: SavingsReward[];
    effective_coupon_value: number;
}

export interface SavingsReward {
    index: number;
    value: number;
    type: string;
    applied_to_purchase_condition: number;
    applied_in_unit_of_measure: string;
    product_restrictions_ref: string[];
}
export enum PromotionAccountType {
    Standard = 'Standard',
    Program = 'Program',
    Reward = 'Reward',
    Alcohol = 'Alcohol'
}

export interface Schedule {
    id: number;
    reference_id: string;
    name: string;
    start_date: string;
    end_date: string;
    expiry_date: string;
    user_selected_start_tz?: string;
    user_selected_end_tz?: string;
    user_selected_expiry_tz?: string;
    resource_group: string | undefined;
}

export interface Pod {
    id: number;
    reference_id: string;
    summary: string; // Savings value
    detail: string; // Purchase description
    resource_group: string | undefined;
    image1_url?: string;
    headline?: string;
    brand_header?: string;
    name?: string;
    print_as_CPG?: boolean;
    print_template?: string;
    concludingMessage?: string;
    packshot_images?: PackshotImage[];
    image?: ImageDetail;
    brand_logo_image?: ImageDetail;
    disclaimer_text?: string;
    tagline?: string;
    pod_image_caption?: string;
}

export interface ImageDetail {
    $ref?: string;
    encoded_bytes?: string;
    image_quality?: string; //  TODO: Can be made an enum
    alt_images?: ImageDetail[];
}

export interface Throttling {
    id: number;
    reference_id: string;
    resource_group: string | undefined;
    name: string;
    applies_to: string;
    type: string;
    limit: number;
    limit_per_transaction?: number;
}

export interface UpdatePackshotPayload {
    promotionId?: number;
    packshotImages: PackshotImage[];
    promotionImages: PromotionImage[];
    podId: number;
}

export interface ProductRestriction {
    id: number;
    company_prefix: string;
    family_code: string;
    gtins: { gtin: string }[];
    skus: { sku: string }[];
}

export interface GtinAdjustment {
    type?: UpcType;
    product_restriction_ref: number;
    include: { gtin: string }[];
    exclude: { gtin: string }[];
}

export interface InlaneSettings {
    type?: InlaneSettingsType;
    code_length?: number;
    is_custom_promo_code?: boolean;
    promo_codes?: string;
}

export interface PromotionAccount {
    id: string;
    name: string;
    type: string; // TODO: enum
}

export enum TargetingType {
    UserTargeted = 'UserTargeted',
    GeoTargeted = 'GeoTargeted',
    National = 'National'
}

export enum SavingsType {
    AmountOff = 'AmountOff',
    BuyXGetYFree = 'BuyXGetYFree',
    PercentOff = 'PercentOff',
    RewardPoints = 'RewardPoints',
    AmountOffPerUnit = 'AmountOffPerUnit',
    PricePoint = 'PricePoint'
}

export interface UpdateOfferUpcPayload {
    promoID: number;
    offerID: number;
    upcs: string[];
    action: 'add' | 'remove';
}

export enum UpcType {
    PRIMARY = 'Primary',
    SECONDARY = 'Secondary'
}

export interface PromotionTrackingProperty {
    created_date: string;
    created_by: number;
    last_updated_date: string;
    last_updated_by: number;
}

export enum InlaneSettingsType {
    PROMOCODE = 'Promocode',
    SWEEPSTAKES = 'Sweepstakes',
    NONE = 'None'
}

export interface TenderType {
    id: number;
    name: string;
    description?: string;
    parent_tender_type?: TenderType;
}

export interface Clearing {
    reference_id: string;
    id: number;
    name: string;
    ocr?: string;
    clearing_house: {
        name: string;
    };
    resource_group: string | undefined;
}

export enum BarcodeType {
    GS1DATABAR_TYPE = 'GS1Databar',
    PLU_TYPE = 'UPC',
    EAN_TYPE = 'EAN',
    CANADIAN_TYPE = 'ITF',
    BARCODELESS_TYPE = 'Barcodeless'
}

export enum PartnerOfferType {
    DGXCODE = 'DGXCODE'
}

export interface ProcessStage {
    name: string;
    last_validation_status: string;
    riq_sync_status: string;
    previous_stage: OfferStage;
    current_stage: OfferStage;
    next_stages: string[];
}

export interface OfferStage {
    name: OfferStageName;
    stage_date: string;
    data: {
        reasons: string[];
        comments?: string[];
    };
}

export interface RetailerBanner {
    status?: RetailerBannerStatus;
    number: string;
    name?: string;
}

export enum RetailerBannerStatus {
    ReadyForClearing = 'ReadyForClearing',
    SentForClearing = 'SentForClearing',
    RejectedFromClearing = 'RejectedFromClearing',
    ReadyToSubmit = 'ReadyToSubmit',
    Pending = 'Pending',
    Approved = 'Approved',
    Rejected = 'Rejected'
}

export interface CPAPromotionUPCEntity {
    promotion_id: number;
    product_restrictions: {
        id: number;
        gtins: { gtin: string }[];
        skus?: { sku: string }[];
    }[];
}

export enum PromotionAdvancedFilterField {
    COMPANY = 'partner_account_id',
    BRAND = 'offer_brand',
    OCR = 'ocr',
    IO_NAME = 'offer_insertion_order_number',
    START_DATE = 'schedule_start_date',
    END_DATE = 'schedule_end_date',
    EXPIRY_DATE = 'schedule_expiry_date',
    PUBLISHING_TYPE = 'offer_publishing_type',
    OFFER_TYPE = 'product_offer_type',
    ACTIVATION_TYPE = 'offer_activation_type',
    OFFER_SUBTYPE = 'offer_placement_type',
    OFFER_STATUS = 'current_stage',
    PID = 'published_pid',
    COUPON_VALUE = 'coupon_value',
    OFFER_SOURCE = 'partner_promotion_source',
    PURCHASE_DESCRIPTION = 'purchase_description'
}

export interface PromotionSearchRequestPayload {
    advancedFilters: AdvancedFilters<PromotionAdvancedFilterField>;
    pagination?: GridPagination;
    sortDescriptor?: SortDescriptor;
}

export enum TypeaheadPicklistType {
    BRAND = 'brand',
    OFFER_SOURCE = 'offer_source',
    OCR = 'ocr',
    PURCHASE_DESCRIPTION = 'pod_detail'
}

export interface UpdateOfferStagePayload {
    offers: OfferPayload[];
    action: OfferStageName;
}

export interface OfferPayload {
    promoId: number;
    offerId: number;
    offerCid: string;
}

export interface PodPreview {
    desktopImage: string;
    podHeader?: string;
    savingsValue: string;
    description: string;
    podId?: number;
}

export interface PackshotImage {
    order?: number;
    index: number;
    image_id: number;
}
export interface PodPackshot {
    index: number;
    image_id: number;
    image_details: PromotionImage;
}

export interface PromotionImages {
    promotion_images: PromotionImage[];
}

export interface PromotionImage {
    id?: number;
    reference_id: string;
    type: string;
    name: string;
    description: string;
    order: number;
    image?: ImageDetail;
}
export interface PodOfferPayloadData {
    podData: PodSectionPreviewPayload;
    cid: string;
}

export interface PodSectionPreviewPayload {
    id: number;
    legalText: string;
    pod: PodPreview;
    name?: string;
    promoId?: number;
    offerId?: number;
}

export interface ProductOfferTypeMapping {
    product_offer_type: ProductOfferType;
    activation_type: ActivationType;
    placement_types: PlacementType[];
}

export interface CompanyPicklistsPayload {
    company_id: number;
    name: string;
    id: string;
    type: string;
    salesforce_id: string;
    attributes: {
        show_external_users_xcode: boolean;
    };
}
